import { useState } from 'react';
import { Plus, Trash2, Copy, CheckCircle2 } from 'lucide-react';
import { createLocationCharger, type ChargerSeatBillingNote } from '@/lib/api';
import { ocppConnectUrl } from '@/lib/ocppConnectUrl';
import styles from './Form.module.css';

/**
 * Location-scoped fork of AddChargerForm.
 *
 * Same identity / vendor / model / OCPP-version / connector editor and the
 * same one-time auth-password reveal with ws connect instructions, but the
 * location is fixed by a `locationId` prop instead of a dropdown, and it
 * submits via createLocationCharger(locationId, body) so a super_admin can
 * add a charger to any location from that location's detail page.
 * AddChargerForm posts to the caller-scoped POST /chargers and so can only
 * reach the caller's own tenant; this one targets the location in the path.
 *
 * Forked rather than parameterised so AddChargerForm and ChargersPage stay
 * byte-unchanged, the same approach as TenantAddLocationForm. No tariff
 * field: AddChargerForm has none either, so the tariff is set later on the
 * charger detail page.
 */

interface LocationAddChargerFormProps {
  locationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const CONNECTOR_TYPES = ['J1772', 'NACS', 'CCS1'];
const OCPP_VERSIONS = ['1.6', '2.0.1'];

interface ConnectorDraft {
  connector_id: number;
  type: string;
  max_power_kw: number;
}

export function LocationAddChargerForm({ locationId, onSuccess, onCancel }: LocationAddChargerFormProps) {
  const [identity, setIdentity] = useState('');
  const [vendor, setVendor] = useState('');
  const [model, setModel] = useState('');
  const [ocppVersion, setOcppVersion] = useState('1.6');
  const [connectors, setConnectors] = useState<ConnectorDraft[]>([
    { connector_id: 1, type: 'J1772', max_power_kw: 11 },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [copied, setCopied] = useState(false);
  // Informational seat note from the create response (never a gate —
  // the charger is registered either way). Shown once under the
  // password card. Same block as AddChargerForm (deliberate fork).
  const [billingNote, setBillingNote] = useState<ChargerSeatBillingNote | null>(null);

  const updateConnector = (index: number, patch: Partial<ConnectorDraft>) => {
    setConnectors((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const addConnector = () => {
    const nextId = Math.max(0, ...connectors.map((c) => c.connector_id)) + 1;
    setConnectors((prev) => [...prev, { connector_id: nextId, type: 'J1772', max_power_kw: 11 }]);
  };

  const removeConnector = (index: number) => {
    if (connectors.length <= 1) return;
    setConnectors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await createLocationCharger(locationId, {
        identity: identity.trim(),
        vendor: vendor.trim(),
        model: model.trim(),
        ocpp_version: ocppVersion,
        connectors: connectors.map((c) => ({
          connector_id: c.connector_id,
          type: c.type,
          max_power_kw: Number(c.max_power_kw),
        })),
      });

      // Display the auth password once, with the seat note beside it.
      setBillingNote(res.billing ?? null);
      setAuthPassword(res.auth_password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create charger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(authPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback — user can still select the text manually
    }
  };

  // Show auth password + done state after successful creation
  if (authPassword) {
    return (
      <div className={styles.form}>
        <div className={styles.formSuccess}>
          <strong>Charger registered successfully.</strong>
        </div>

        <div className={styles.authCard}>
          <p className={styles.authLabel}>OCPP Auth Password</p>
          <div className={styles.authValue}>{authPassword}</div>
          <p className={styles.authNote}>
            Save this password now and configure your charger's OCPP Basic auth with identity{' '}
            <code>{identity}</code> and this password. Then point it at{' '}
            <code>{ocppConnectUrl(identity)}</code>. You can reveal this password again from the
            charger page.
          </p>
          <button type="button" className={styles.btnSecondary} onClick={handleCopy}>
            {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy password</>}
          </button>
        </div>

        {/* Quiet seat note: covered count for a subscribed tenant, or the
            subscribe nudge. Informational only — the charger above is
            already registered. The billing path is PLAIN TEXT, not a link:
            this sits directly under the one-time OCPP password, and a link
            here navigated away before the admin had copied it. They go
            there after copying. Same block as AddChargerForm (deliberate
            fork). */}
        {billingNote && (
          <p className={styles.hint}>
            {billingNote.message}
            {billingNote.code === 'SUBSCRIPTION_REQUIRED' && (
              <> (<code>{billingNote.subscribe_url}</code>)</>
            )}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className={styles.btnPrimary} onClick={onSuccess}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {error && <div className={styles.formError}>{error}</div>}

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Identity</label>
          <input
            className={styles.input}
            type="text"
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            placeholder="e.g. M3P-001"
            required
            maxLength={20}
          />
          <p className={styles.hint}>OCPP chargepoint ID (max 20 chars, no spaces)</p>
        </div>
        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>OCPP Version</label>
          <select
            className={styles.select}
            value={ocppVersion}
            onChange={(e) => setOcppVersion(e.target.value)}
            required
          >
            {OCPP_VERSIONS.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Vendor</label>
          <input
            className={styles.input}
            type="text"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="e.g. Weeyu"
            required
          />
        </div>
        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Model</label>
          <input
            className={styles.input}
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="e.g. M3P"
            required
          />
        </div>
      </div>

      <div>
        <label className={styles.label} style={{ marginBottom: 10, display: 'block' }}>
          Connectors <span style={{ color: 'var(--color-brand)' }}>*</span>
        </label>

        <div className={styles.connectorList}>
          <div className={styles.connectorRowHeader}>
            <span>#</span>
            <span>Type</span>
            <span>Max Power (kW)</span>
            <span></span>
          </div>

          {connectors.map((c, i) => (
            <div key={i} className={styles.connectorRow}>
              <input
                className={styles.input}
                type="number"
                min={1}
                value={c.connector_id}
                onChange={(e) => updateConnector(i, { connector_id: parseInt(e.target.value, 10) || 1 })}
              />
              <select
                className={styles.select}
                value={c.type}
                onChange={(e) => updateConnector(i, { type: e.target.value })}
              >
                {CONNECTOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                className={styles.input}
                type="number"
                min={0.1}
                step={0.1}
                value={c.max_power_kw}
                onChange={(e) => updateConnector(i, { max_power_kw: parseFloat(e.target.value) || 0 })}
              />
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeConnector(i)}
                disabled={connectors.length <= 1}
                aria-label="Remove connector"
                title="Remove connector"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <button type="button" className={styles.addBtn} onClick={addConnector} style={{ marginTop: 10 }}>
          <Plus size={14} /> Add connector
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="submit"
          className={styles.btnPrimary}
          disabled={submitting || !identity || !vendor || !model}
        >
          {submitting ? 'Registering...' : 'Register Charger'}
        </button>
      </div>
    </form>
  );
}
